import React from 'react';
import { Modal, Button, Badge, Card, Row, Col } from 'react-bootstrap';

// Weights for the 4 chain timeframes (must match backend)
const CHAIN_WEIGHTS = { '1h': 0.25, '4h': 0.5, 'D': 1, 'W': 2 };

const SignalDetails = ({ signal, allSignals = [], show, onHide }) => {
  if (!signal) {
    return null;
  }
  
  // Helper function to format date
  const formatDate = (dateString) => {
    const options = { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric', 
      hour: '2-digit', 
      minute: '2-digit',
      second: '2-digit',
      timeZoneName: 'short'
    };
    return new Date(dateString).toLocaleString(undefined, options);
  };
  
  // Helper function to convert symbol to uppercase
  const formatSymbol = (symbol) => {
    return symbol ? symbol.toUpperCase() : '';
  };
  
  // Get display configuration based on signal type
  const getSignalConfig = (signalType) => {
    if (signalType === 'long') {
      return { 
        color: '#28a745', 
        icon: '↗️',
        description: 'Long signal indicates a potential upward price movement.'
      };
    } else if (signalType === 'short') {
      return { 
        color: '#dc3545', 
        icon: '↘️',
        description: 'Short signal indicates a potential downward price movement.'
      };
    } else if (signalType === 'mean reversal long') {
      return { 
        color: '#20c997', 
        icon: '↗️',
        description: 'Mean reversal long signal indicates a potential upward price movement after a downward trend.'
      };
    } else if (signalType === 'mean reversal short') {
      return { 
        color: '#fd7e14', 
        icon: '↘️',
        description: 'Mean reversal short signal indicates a potential downward price movement after an upward trend.'
      };
    } else if (signalType === 'strong mean reversal long') {
      return { 
        color: '#157a59', 
        icon: '⬆️',
        description: 'Strong mean reversal long signal indicates a high-confidence upward price movement after a downward trend.'
      };
    } else if (signalType === 'strong mean reversal short') {
      return { 
        color: '#b35a0f', 
        icon: '⬇️',
        description: 'Strong mean reversal short signal indicates a high-confidence downward price movement after an upward trend.'
      };
    } else if (signalType === 'kernel change bull') {
      return { 
        color: '#0f5132', 
        icon: '🐂',
        description: 'Kernel change bull signal indicates a fundamental change to a bullish market structure.'
      };
    } else if (signalType === 'kernel change bear') {
      return { 
        color: '#842029', 
        icon: '🐻',
        description: 'Kernel change bear signal indicates a fundamental change to a bearish market structure.'
      };
    }
    
    // Default
    return { 
      color: '#6c757d', 
      icon: '🔍',
      description: 'Signal type information not available.'
    };
  };
  
  // Get configuration for current signal
  const signalConfig = getSignalConfig(signal.signal);

  // Chain confluence metadata stored on the signal
  const chainWeightUp = signal.chain_weight_up || 0;
  const chainWeightDown = signal.chain_weight_down || 0;
  const chainTfsUp = signal.chain_timeframes_up || [];
  const chainTfsDown = signal.chain_timeframes_down || [];
  const chainDirection = signal.chain_direction || null;
  const hasChain = chainWeightUp > 0 || chainWeightDown > 0;
  const hasBothChains = chainWeightUp > 0 && chainWeightDown > 0;
  
  return (
    <Modal
      show={show}
      onHide={onHide}
      size="lg"
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title>
          <span className="me-2">{signalConfig.icon}</span>
          {formatSymbol(signal.symbol)} Signal Detail
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="signal-detail-header mb-4">
          <h2 className="mb-3">
            {formatSymbol(signal.symbol)}
            <Badge 
              className="ms-3"
              style={{ 
                backgroundColor: `${signalConfig.color}20`, 
                color: signalConfig.color,
                padding: '8px 12px',
                fontSize: '0.9rem'
              }}
            >
              {signal.signal}
            </Badge>
            
            {chainWeightUp > 0 && (
              <Badge className="ms-2 chain-badge chain-badge-up" style={{ padding: '8px 12px', fontSize: '0.9rem' }}>
                {chainWeightUp}x↑
              </Badge>
            )}
            {chainWeightDown > 0 && (
              <Badge className="ms-2 chain-badge chain-badge-down" style={{ padding: '8px 12px', fontSize: '0.9rem' }}>
                {chainWeightDown}x↓
              </Badge>
            )}
          </h2>
          <p className="text-muted mb-0">
            Signal received at {formatDate(signal.timestamp)}
          </p>
        </div>
        
        <Card className="mb-4">
          <Card.Body>
            <h5 className="mb-3">Signal Information</h5>
            <div className="row">
              <div className="col-md-6 mb-3">
                <div className="d-flex justify-content-between">
                  <strong>Price:</strong>
                  <span>${parseFloat(signal.price).toFixed(2)}</span>
                </div>
              </div>
              <div className="col-md-6 mb-3">
                <div className="d-flex justify-content-between">
                  <strong>Signal Type:</strong>
                  <span>{signal.signal}</span>
                </div>
              </div>
              <div className="col-md-6 mb-3">
                <div className="d-flex justify-content-between">
                  <strong>Timeframe:</strong>
                  <span>{signal.timeframe || 'Not specified'}</span>
                </div>
              </div>
              <div className="col-md-6 mb-3">
                <div className="d-flex justify-content-between">
                  <strong>Signal ID:</strong>
                  <code className="text-muted">{signal.id}</code>
                </div>
              </div>
              <div className="col-md-6 mb-3">
                <div className="d-flex justify-content-between">
                  <strong>Received:</strong>
                  <span>{formatDate(signal.received || signal.timestamp)}</span>
                </div>
              </div>
            </div>
          </Card.Body>
        </Card>
        
        {/* Timeframe chain alignment card */}
        {hasChain && (
          <Card className="mb-4 chain-alignment-card">
            <Card.Header style={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)', color: 'white' }}>
              <h5 className="mb-0">
                <i className="bi bi-diagram-3-fill me-2"></i>
                Timeframe Alignment
              </h5>
            </Card.Header>
            <Card.Body>
              {hasBothChains && (
                <div className="alert alert-warning mb-3">
                  <i className="bi bi-exclamation-triangle-fill me-2"></i>
                  <strong>Conflicting signals:</strong> Both bullish and bearish chains exist across timeframes. Exercise caution.
                </div>
              )}

              <Row className="g-3">
                {chainWeightUp > 0 && (
                  <Col md={hasBothChains ? 6 : 12}>
                    <div className="chain-direction-block chain-direction-up">
                      <div className="chain-direction-header">
                        <span>↑ Bullish Chain</span>
                        <span className="chain-weight-display">{chainWeightUp}x</span>
                      </div>
                      <div className="chain-timeframe-list">
                        {chainTfsUp.map(tf => (
                          <div key={tf} className="chain-timeframe-item">
                            <span className="chain-tf-label">{tf}</span>
                            <span className="chain-tf-weight">{CHAIN_WEIGHTS[tf]}x</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </Col>
                )}

                {chainWeightDown > 0 && (
                  <Col md={hasBothChains ? 6 : 12}>
                    <div className="chain-direction-block chain-direction-down">
                      <div className="chain-direction-header">
                        <span>↓ Bearish Chain</span>
                        <span className="chain-weight-display">{chainWeightDown}x</span>
                      </div>
                      <div className="chain-timeframe-list">
                        {chainTfsDown.map(tf => (
                          <div key={tf} className="chain-timeframe-item">
                            <span className="chain-tf-label">{tf}</span>
                            <span className="chain-tf-weight">{CHAIN_WEIGHTS[tf]}x</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </Col>
                )}
              </Row>

              {!hasBothChains && (
                <div className="alert alert-info mt-3">
                  <i className="bi bi-info-circle-fill me-2"></i>
                  <strong>Multi-timeframe confluence</strong> indicates a stronger {chainDirection === 'up' ? 'bullish' : 'bearish'} trend.
                  When signals align across timeframes, they often indicate a more reliable trading opportunity.
                </div>
              )}
            </Card.Body>
          </Card>
        )}
        
        <div className="signal-description p-3" style={{ backgroundColor: `${signalConfig.color}10`, borderRadius: '8px' }}>
          <h5 className="mb-3">Signal Description</h5>
          <p className="mb-0">{signalConfig.description}</p>
        </div>
        
        {signal.notes && (
          <div className="mt-4">
            <h5>Notes</h5>
            <p>{signal.notes}</p>
          </div>
        )}
        
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default SignalDetails;